'use client';
import classNames from 'classnames';
import { UserActionEnum } from '../../utils/analytics';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { formatData } from '../../utils/helpers';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import { useBreakpoint } from '../../utils/useBreakpoint';
import Button from '../Button';
import { SlideUpModal } from '../SlideUpModal';
import { ApprovalOddsValidity } from './components/approvalOddsValidity';
import { ChanceCard } from './components/chanceCard';
import { TRANSLATIONS } from './i18n';
import { ApprovalOddsDrawerProps } from './types';

// Shared EN/ZH terms & conditions page for approval odds (hosted on a single URL).
const APPROVAL_ODDS_TNC_URL = 'https://moneyhero.link/approval-odds-tnc';

export const ApprovalOddsDrawer = ({ slideUpModalRef, onClose, onClickCTA, lastUpdated, isOutdated, pushDataLayer }: ApprovalOddsDrawerProps) => {
  const activeBreakpoint = useBreakpoint({
    _: '_',
    sm: 'sm',
    md: 'md',
    lg: 'lg',
    xl: 'xl',
    xxl: 'xxl',
  });

  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const chance = ['HIGH', 'MEDIUM', 'LOW', 'UNKNOWN'] as const;
  
  if (!lastUpdated) {
    return null;
  }

  const dateFormat = tr('approval_odds_date_format', staticText);
  const formattedDate = formatData(
    lastUpdated,
    dateFormat,
    context.lang,
  );

  return (
    <SlideUpModal
      title={tr('popup_title', staticText)}
      titleClassName="font-font-weight/subheading-1 text-font-size/subheading-1 leading-line-height/subheading-1"
      ref={slideUpModalRef}
      onClose={onClose}
      titleContainerClassName=" border-b border-borders/default !px-spacing/padding/x !py-spacing/padding/y-sm"
      rootClassName="!p-[0px] no-scrollbar !flex !flex-col"
      bodyClassName="!p-0 !overflow-y-auto !flex-1"
    >
      <div className="flex-1">
        <div className="flex flex-col gap-spacing/gap/section-y px-spacing/gap/x pt-spacing/margin/y">
          <ApprovalOddsValidity isOutdated={isOutdated} />
          {chance.map((chanceType, index) => {
            return <ChanceCard key={chanceType} type={chanceType} isFresh={!isOutdated} showBorder={index !== chance.length - 1} />;
          })}
        </div>
        <div className="flex flex-col gap-spacing/gap/soft-12px border-y border-borders/default">
          <div className="flex flex-col px-spacing/gap/x py-spacing/gap/section-y gap-spacing/gap/soft-12px">
            <div className="mhg-alpha-typography-bullet-2 text-text/primary">{tr('approval_odds_determined', staticText)}</div>
            <div className="mhg-alpha-typography-body-2-relaxed text-text/secondary-lighter">
              {tr('determined_description', staticText)}
            </div>
          </div>
        </div>
        <div className="mhg-alpha-typography-caption flex flex-col gap-spacing/gap/soft-12px px-spacing/gap/x py-spacing/gap/section-y">
          <div>
            {tr('last_updated_label', staticText)}
            {lastUpdated ? formattedDate : '-'}
          </div>
          <div>
            {tr('disclaimer', staticText)}
            <a
              href={APPROVAL_ODDS_TNC_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              {tr('disclaimer_tnc_link', staticText)}
            </a>
          </div>
        </div>
      </div>
      {isOutdated && (
        <div
            className={classNames('flex flex-col items-center gap-spacing/gap/x px-spacing/padding/x py-spacing/padding/y-sm sticky bottom-0 bg-white', {
              'shadow-[0px_0px_12px_-1px_rgba(0,0,0,0.25)]': activeBreakpoint === 'sm' || activeBreakpoint === '_',
            })}
          >
            <div className="mhg-alpha-typography-caption text-text/primary">{tr('button_mini_description', staticText)}</div>
            <Button
              className="w-full px-button/padding-regular-x text-text/on-dark"
              variant="filled"
              styleType="primary"
              onClick={() => {
                pushDataLayer?.({
                  event:'credit_report_interaction',
                  intent:'Refresh Credit Data',
                  action:UserActionEnum.CLICK,
                  component_type:'drawer',
                  component_title:'CHC Approval Odds Explanation Drawer/Footer CTA_Refresh My Credit Data',
                  component_index:'1'
                })
                onClickCTA?.()
              }}
            >
              {tr('button_label', staticText)}
            </Button>
        </div>
      )}
    </SlideUpModal>
  );
};